import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_VARIANTS } from "@shared/ui/Button";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import React, { useState } from "react";

import { Dialog, DialogContent } from "./Dialog";

export type ConfirmDialogVariant = "danger" | "primary";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Illustration rendered at 130×130 on the left. */
  illustration?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** @default "Cancel" */
  cancelLabel?: React.ReactNode;
  confirmLabel: React.ReactNode;
  /** @default "danger" */
  variant?: ConfirmDialogVariant;
  /**
   * Confirm handler. If it returns a promise the button is disabled while
   * pending. Modal closes automatically after the handler resolves.
   */
  onConfirm: () => void | Promise<void>;
}

/**
 * Reusable confirmation dialog with the design-system illustration on the
 * left and title/description/buttons on the right. Use for "are you sure?"
 * prompts (unlink, remove, delete, etc.).
 *
 *   <ConfirmDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     illustration={<RemoveGhostIcon />}
 *     title="Unlink Work Order?"
 *     description="Are you sure you want to unlink this work order?"
 *     confirmLabel="Unlink"
 *     onConfirm={handleUnlink}
 *   />
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  illustration,
  title,
  description,
  cancelLabel = "Cancel",
  confirmLabel,
  variant = "danger",
  onConfirm,
}: ConfirmDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    try {
      setSubmitting(true);
      await onConfirm();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} showCloseButton={false}>
      <DialogContent maxWidth="max-w-[556px]">
        <div className="flex items-center gap-sm p-2xl">
          {illustration && (
            <div className="shrink-0 w-[130px] h-[130px] flex items-center justify-center">
              {illustration}
            </div>
          )}
          <div className="flex flex-col gap-2xl flex-1 min-w-0">
            <div className="flex flex-col gap-sm">
              <Headline size={HEADLINE_SIZES.X_SMALL}>{title}</Headline>
              {description && (
                <BodyText size={BODY_TEXT_SIZES.SMALL} color="text-secondary">
                  {description}
                </BodyText>
              )}
            </div>
            <div className="flex items-center justify-end gap-md">
              <Button
                variant={BUTTON_VARIANTS.OUTLINE}
                text={cancelLabel}
                disabled={submitting}
                onClick={() => onOpenChange(false)}
              />
              <Button
                variant={
                  variant === "danger"
                    ? BUTTON_VARIANTS.DANGER
                    : BUTTON_VARIANTS.PRIMARY
                }
                text={confirmLabel}
                disabled={submitting}
                onClick={handleConfirm}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
